import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEqual } from 'lodash';
import {
  combineLatest,
  EMPTY,
  Observable,
  of,
  Subject,
  switchMap,
  timer,
} from 'rxjs';
import { debounceTime, map, pairwise, tap } from 'rxjs/operators';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../../../core/h3ml/state/task-workspace-store';
import { AttributeHelper } from '../../../../../core/shared/attribute-helper/attribute-helper';
import { getAttributeHelper } from '../../../../../core/shared/attribute-helper/get-attribute-helper';
import {
  Tag,
  TagsState,
} from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { getDynamicTag } from '../../../../../cross-team-shared/misc-services/tag-helper';
import { Attribute } from '../../../../../team-apis/otterdam/taxonomy';

export const animationDebounceTimeMs = 300;

@Component({
  selector: 'h3-attribute-display-label',
  templateUrl: './attribute-display-label.component.html',
  styleUrls: ['./attribute-display-label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AttributeDisplayLabelComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) attribute!: Attribute;
  @Input({ required: true }) annotationStore!: Store<TagsState>;
  @Input({ required: true }) attributeValue!: Tag;

  isDifferentFromDefault$!: Observable<boolean>;
  isInvalidValue$!: Observable<boolean>;
  isDynamicFrame$!: Observable<boolean>;
  hasValueChanged$!: Observable<boolean>;
  private attributeHelper!: AttributeHelper;
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private frameNumberStore = inject(fusionStoreToken)('frameNumber');

  ngOnInit() {
    this.attributeHelper = getAttributeHelper(this.attribute.__type);

    this.isDynamicFrame$ = this.attribute.isDynamic
      ? this.buildIsDynamicFrame$()
      : of(false);

    const defaultValue = this.attributeHelper.getDefaultOptionValue(
      this.attribute,
    );
    this.isDifferentFromDefault$ = this.getInput$('attributeValue').pipe(
      map((value) => !isEqual(value, defaultValue)),
    );

    this.isInvalidValue$ = this.getInput$('attributeValue').pipe(
      map((value) =>
        this.attributeHelper.isInvalidValue(value, this.attribute),
      ),
    );

    this.hasValueChanged$ = this.buildHasValueChanged$();
  }

  protected onLabelClick(event: MouseEvent) {
    if (this.taskWorkspaceStore.state().readOnly) {
      event.stopPropagation();
    }
  }

  private buildIsDynamicFrame$() {
    return this.getInput$('annotationStore').pipe(
      switchMap((annotationStore) =>
        combineLatest([
          annotationStore('dynamic_tags')(this.attribute.name).$,
          this.frameNumberStore.$,
        ]).pipe(
          map(([frameSpecificTags, frameNumber]) => {
            if (!frameSpecificTags) {
              return false;
            }
            return frameSpecificTags.some(
              ({ frame_number }) => frame_number === frameNumber,
            );
          }),
        ),
      ),
    );
  }

  private buildHasValueChanged$() {
    // only supports attribute changes when scrubbing
    if (!this.attribute.isDynamic) {
      return EMPTY;
    }

    const hasValueChanged$ = new Subject<boolean>();

    const changedValueTrigger$ = this.buildChangeValueTrigger().pipe(
      tap((hasValueChanged) => hasValueChanged$.next(hasValueChanged)),
    );

    // clear observable so the animation can be triggered again on frame change
    const animationTimeMs = 1000;
    this.subscribeTo(
      changedValueTrigger$.pipe(switchMap(() => timer(animationTimeMs + 100))),
      () => hasValueChanged$.next(false),
    );

    return hasValueChanged$.asObservable();
  }

  private buildChangeValueTrigger() {
    return this.frameNumberStore.$.pipe(
      pairwise(),
      debounceTime(animationDebounceTimeMs),
      map(([currentFrame, previousFrame]) => {
        const frameSpecificTag =
          this.annotationStore.state().dynamic_tags[this.attribute.name];
        const previousValue = getDynamicTag(frameSpecificTag, previousFrame);
        const currentValue = getDynamicTag(frameSpecificTag, currentFrame);
        return (
          previousFrame !== currentFrame &&
          !isEqual(previousValue, currentValue)
        );
      }),
    );
  }
}
